import type { DashboardBanner } from "@/content/dashboardBanners";
import { storedTimes } from "./storedTimes";

/**
 * When each banner with a countdown from the first view was first seen, by
 * the banner's id: two banners on one flag run their own timers.
 */
const starts = storedTimes("dashboardBannerCountdowns");

/**
 * When the banner's countdown runs out, in ms since the epoch. A countdown
 * from the first view that has not started yet runs out its full length from
 * `now`; null for a banner with no countdown.
 */
export const countdownEnd = (banner: DashboardBanner, now: number) => {
  const countdown = "countdown" in banner ? banner.countdown : null;
  if (!countdown) return null;
  const { runs } = countdown;
  if (runs.kind === "deadline") return Date.parse(runs.at);
  return (starts.read()[banner.id] ?? now) + runs.minutes * 60_000;
};

/** Starts a countdown from the first view; a started one keeps its start. */
export const startCountdown = (banner: DashboardBanner, now: number) => {
  const countdown = "countdown" in banner ? banner.countdown : null;
  if (countdown?.runs.kind !== "session" || banner.id in starts.read()) return;
  starts.write({ ...starts.read(), [banner.id]: now });
};

/** A banner whose countdown ran out and is set to hide then. */
export const isHiddenByCountdown = (banner: DashboardBanner, now: number) => {
  const end = countdownEnd(banner, now);
  return (
    end !== null &&
    end <= now &&
    "countdown" in banner &&
    banner.countdown?.ends.kind === "hide"
  );
};

/**
 * Forgets the starts of banners no longer published. A failed read of the
 * banners also answers none, so with none published nothing is forgotten.
 */
export const forgetUnpublishedCountdowns = (published: DashboardBanner[]) => {
  if (published.length === 0) return;
  starts.keepOnly(new Set(published.map(({ id }) => id)));
};
